import { MessageCircle } from 'lucide-react';
import type { VisitorConfig } from '../../types';

// Bouton de chat flottant, bas gauche, présent sur toutes les pages du site
// public (voir ses points de montage : VisitorPortal et PageRenderer, qui
// partagent déjà SiteFooter).
//
// Ouvre une conversation WhatsApp, mais rien ici ne le dit : ni l'icône (celle
// importée par l'agence, jamais le logo WhatsApp), ni le texte affiché au
// visiteur. Seul le back-office nomme WhatsApp, puisque c'est bien de ça qu'il
// s'agit pour l'agence qui configure le numéro — voir BackOfficeAdmin.tsx.

/** wa.me n'accepte que des chiffres : l'agence peut coller un numéro avec espaces, +, parenthèses. */
function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

interface FloatingChatButtonProps {
  config: VisitorConfig;
  /** Décalage supplémentaire, en pixels, pour rester au-dessus du bandeau cookies quand il est affiché. */
  liftPx?: number;
}

export default function FloatingChatButton({ config, liftPx = 0 }: FloatingChatButtonProps) {
  const phone = digitsOnly(config.chatButtonWhatsappNumber || '');
  if (!config.chatButtonEnabled || !phone) return null;

  const message = config.chatButtonMessage?.trim();
  const href = `https://wa.me/${phone}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
  const text = config.chatButtonText?.trim();
  const label = text || 'Discutez avec nous';
  const bottom = 20 + liftPx;

  // Icône seule (rond) quand il n'y a pas de texte, pilule icône + texte sinon
  // — un rond qui porterait du texte serait illisible passé quelques lettres.
  if (!text) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        title={label}
        className="fixed left-5 z-40 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105"
        style={{ bottom, backgroundColor: config.chatButtonIconUrl ? 'transparent' : config.brandColor }}
      >
        {config.chatButtonIconUrl ? (
          <img src={config.chatButtonIconUrl} alt="" className="w-full h-full rounded-full object-cover" />
        ) : (
          <MessageCircle className="w-7 h-7 text-white" />
        )}
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      className="fixed left-5 z-40 flex items-center gap-2.5 rounded-full bg-white shadow-lg pl-1.5 pr-4 py-1.5 transition-transform hover:scale-105"
      style={{ bottom }}
    >
      {config.chatButtonIconUrl ? (
        <img src={config.chatButtonIconUrl} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
      ) : (
        <span
          className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: config.brandColor }}
        >
          <MessageCircle className="w-4.5 h-4.5 text-white" />
        </span>
      )}
      <span className="text-sm font-bold text-slate-800 whitespace-nowrap">{text}</span>
    </a>
  );
}
